import { toJalaali } from "jalaali-js";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import useAdminDashboard from "../../hooks/useAdminDashboard.js";

import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
} from "recharts";

// const toJalaali = jalaaliModule.toJalaali || jalaaliModule.default?.toJalaali;

import "../../styles/AdminPanel/AdminDashboard.css";
import { useTicketSocket } from "../../context/TicketSocketContext";

/* =========================================================
   SHARED CHART STYLES
   ========================================================= */

const axisTick = {
  fontSize: 10.5,
  fill: "rgba(43, 18, 76, 0.5)",
};

const yAxisTick = {
  fontSize: 10.5,
  fill: "rgba(43, 18, 76, 0.5)",
  textAnchor: "start",
};

const Icon = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);
const ICONS = {
  externalLink: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4L10 14" />
      <path d="M18 13v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h5" />
    </>
  ),
};
/* =========================================================
   MONTH TICK
   =========================================================
   اسم ماه‌ها به‌صورت عمودی (چرخش ۹۰ درجه) زیر محور X
   نمایش داده می‌شوند تا با هم تداخل نداشته باشند.
   ========================================================= */

const MonthTick = ({ x, y, payload }) => {
  return (
    <g className="adminx-month-tick" transform={`translate(${x},${y})`}>
      <text
        x={-26}
        y={0}
        textAnchor="start"
        transform="rotate(-90)"
        fill="rgba(43, 18, 76, 0.5)"
        fontSize={10}
        fontFamily="inherit"
      >
        {payload?.value}
      </text>
    </g>
  );
};

/* =========================================================
   TOOLTIP
   ========================================================= */

const ChartTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;

  const fullDate = payload[0].payload?.date;
  const title = fullDate ? toFaDigits(fullDate) : label;

  return (
    <div className="chart-tooltip">
      {title && <span className="chart-tooltip-label">{title}</span>}

      <span className="chart-tooltip-value">
        {Number(payload[0].value).toLocaleString("fa-IR")}
        {unit ? ` ${unit}` : ""}
      </span>
    </div>
  );
};

/* =========================================================
   ICONS
   ========================================================= */

const iconProps = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

function FiShoppingBag() {
  return (
    <svg {...iconProps}>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

function FiUsers() {
  return (
    <svg {...iconProps}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function FiTrendingUp() {
  return (
    <svg {...iconProps}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}

function FiHeadphones() {
  return (
    <svg {...iconProps}>
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5Z" />
      <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5Z" />
    </svg>
  );
}

function FiMessageSquare() {
  return (
    <svg {...iconProps}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
    </svg>
  );
}

function FiClock() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function FiDollarSign() {
  return (
    <svg {...iconProps}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

/* =========================================================
   CONSTANTS
   ========================================================= */

const MONTHS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

/* =========================================================
   VISITS + TOP STORES — CONSTANTS
   =========================================================
   فیلدهای ریسپانس بک‌اند:
     data.visits_by_month      → [{ year, month: 1..12, count }]
     data.visits_last_30_days  → [{ date, jalali_date, day, count }]
   تا وقتی داده نرسیده، نمودار با مقدار صفر نمایش داده می‌شود.
   ========================================================= */

const toFaDigits = (v) => String(v).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

const EMPTY_MONTHLY_VISITS = Array.from({ length: 30 }, (_, i) => ({
  day: (i + 1).toLocaleString("fa-IR"),
  value: 0,
}));

const EMPTY_YEARLY_VISITS = MONTHS.map((m) => ({ day: m, value: 0 }));

/* هر فیلتر به یک لیست از ریسپانس بک‌اند وصل است */
const STORE_SORT_SOURCES = {
  visits: { list: "top_stores_by_views", field: "total_views" },
  customers: { list: "top_stores_by_customers", field: "total_customers" },
  sales: { list: "top_stores_by_sales", field: "total_sales" },
};

const formatStat = (v) =>
  v === null || v === undefined ? "—" : Number(v).toLocaleString("fa-IR");

const STORE_SORT_OPTIONS = [
  { key: "visits", label: "بیشترین بازدید" },
  { key: "customers", label: "بیشترین مشتری" },
  { key: "sales", label: "بیشترین فروش" },
];

/* =========================================================
   TOAST
   ========================================================= */

function Toast({ notifications, onDismiss }) {
  return (
    <div className="adminx-toast-stack">
      {notifications.map((n) => (
        <div
          key={n.id}
          className="adminx-toast"
          onClick={() => onDismiss(n.id)}
        >
          <span>{n.text}</span>
          <span className="adminx-toast-close">×</span>
        </div>
      ))}
    </div>
  );
}

/* =========================================================
   STORE AVATAR
   ========================================================= */

function StoreAvatar({ src, name }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span className="adminx-store-avatar adminx-store-avatar-fallback">
        {(name || "?").trim().charAt(0)}
      </span>
    );
  }

  return (
    <img
      className="adminx-store-avatar"
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

/* =========================================================
   VISITS RADAR CHART
   ========================================================= */

const VisitsRadarChart = ({ data, unit }) => (
  <div className="adminx-visits-box">
    <ResponsiveContainer width="100%" height="100%">
      <RadarChart data={data} outerRadius="72%">
        <defs>
          <linearGradient id="adminVisitsRadarFill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--rose-500)" stopOpacity={0.55} />
            <stop
              offset="100%"
              stopColor="var(--mauve-700)"
              stopOpacity={0.35}
            />
          </linearGradient>
        </defs>

        <PolarGrid stroke="rgba(43, 18, 76, 0.12)" />

        <PolarAngleAxis dataKey="day" tick={axisTick} tickLine={false} />

        <Tooltip content={<ChartTooltip unit={unit} />} />

        <Radar
          dataKey="value"
          stroke="var(--mauve-700)"
          strokeWidth={2}
          fill="url(#adminVisitsRadarFill)"
          dot={{
            r: 2.5,
            fill: "var(--mauve-700)",
            stroke: "var(--white)",
            strokeWidth: 1,
          }}
        />
      </RadarChart>
    </ResponsiveContainer>
  </div>
);

/* =========================================================
   TICKET STATUS
   ========================================================= */

function TicketStatusDonut() {
  return null;
}

/* =========================================================
   MAIN DASHBOARD
   ========================================================= */

export default function AdminDashboard() {
  const now = new Date();

  const { jy: currentJYear, jm: currentJMonth } = toJalaali(
    now.getFullYear(),
    now.getMonth() + 1,
    15,
  );

  const [filters, setFilters] = useState({
    store_year: currentJYear,
    ticket_year: currentJYear,
    payment_year: currentJYear,
    marketer_year: currentJYear,
    marketer_month: currentJMonth,
  });

  const [notifications, setNotifications] = useState([]);

  // نمودار بازدید: monthly = روزانه (۳۰ روز اخیر) ، yearly = ماهانه (۱۲ ماه)
  const [visitsPeriod, setVisitsPeriod] = useState("monthly");

  // فیلتر جدول فروشگاه‌های فعال
  const [storeSort, setStoreSort] = useState("visits");

  const filtersRef = useRef(filters);
  const marketerSectionRef = useRef(null);

  const { data, loading, refetch } = useAdminDashboard(filters);
  const lastMessage = useTicketSocket();

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  /* =========================================================
     WEBSOCKET
     اتصال واقعی داخل TicketSocketContext برقرار می‌شه (همون که
     TicketNotificationPopup هم ازش استفاده می‌کنه)، اینجا فقط
     به پیام‌های تازه گوش می‌دیم.
     ========================================================= */

  // useEffect(() => {
  //   if (!lastMessage) return;

  //   try {
  //     const msg = lastMessage.data;
  //     let text;

  //     if (msg.event === "new_ticket") {
  //       text = `🎫 تیکت جدید: ${msg.title}`;
  //     } else if (msg.event === "new_message") {
  //       text = `💬 پیام جدید در تیکت: ${msg.title}`;
  //     } else if (msg.event === "new_consultation") {
  //       text = `📋 ${msg.source_label} جدید از ${msg.full_name}`;
  //     } else {
  //       return;
  //     }

  //     setNotifications((prev) => [
  //       {
  //         id: Date.now(),
  //         text,
  //       },
  //       ...prev.slice(0, 4),
  //     ]);

  //     refetch(filtersRef.current);
  //   } catch (error) {
  //     console.error("خطا در پردازش پیام WebSocket:", error);
  //   }
  // }, [lastMessage, refetch]);

  // const dismiss = (id) => {
  //   setNotifications((prev) => prev.filter((n) => n.id !== id));
  // };

  /* =========================================================
     FILTER HANDLER
     ========================================================= */

  const set = useCallback(
    (key) => (e) => {
      const val = Number(e.target.value);

      const isMarketer = key === "marketer_year" || key === "marketer_month";

      if (isMarketer && marketerSectionRef.current) {
        const rect = marketerSectionRef.current.getBoundingClientRect();

        const scrollTop = window.scrollY + rect.top;

        setFilters((f) => ({
          ...f,
          [key]: val,
        }));

        requestAnimationFrame(() => {
          window.scrollTo({
            top: scrollTop - 80,
            behavior: "instant",
          });
        });
      } else {
        setFilters((f) => ({
          ...f,
          [key]: val,
        }));
      }
    },
    [],
  );

  const jalaliYears = data?.available_years || [];

  /* =========================================================
     VISITS CHART DATA
     ========================================================= */

  // ۱۲ ماه: بک‌اند ۱۲ ماه اخیر را می‌دهد (مثلاً مهر ۱۴۰۵ تا آبان ۱۴۰۴)،
  // اینجا بر اساس شماره‌ی ماه به ترتیب فروردین → اسفند چیده می‌شود.
  const yearlyVisits = useMemo(() => {
    const rows = data?.visits_by_month;
    if (!rows?.length) return EMPTY_YEARLY_VISITS;

    const byMonth = new Map(rows.map((d) => [d.month, Number(d.count) || 0]));

    return MONTHS.map((name, i) => ({
      day: name,
      value: byMonth.get(i + 1) ?? 0,
    }));
  }, [data?.visits_by_month]);

  // ۳۰ روز اخیر: به همان ترتیب بک‌اند
  const monthlyVisits = useMemo(() => {
    const rows = data?.visits_last_30_days;
    if (!rows?.length) return EMPTY_MONTHLY_VISITS;

    return rows.map((d) => ({
      day: Number(d.day).toLocaleString("fa-IR"),
      date: d.jalali_date,
      value: Number(d.count) || 0,
    }));
  }, [data?.visits_last_30_days]);

  const visitsChartData =
    visitsPeriod === "yearly" ? yearlyVisits : monthlyVisits;

  /* =========================================================
     TOP 5 ACTIVE STORES (sorted client-side — بدون رفرش صفحه)
     ========================================================= */

  const topStores = useMemo(() => {
    // اطلاعات هر فروشگاه را از هر سه لیست جمع می‌کنیم تا ستون‌های
    // دیگر هم (در صورت حضور فروشگاه در آن لیست) پر شوند.
    const byId = new Map();

    Object.entries(STORE_SORT_SOURCES).forEach(([metric, { list, field }]) => {
      (data?.[list] ?? []).forEach((s) => {
        const row = byId.get(s.store_id) ?? {
          id: s.store_id,
          name: s.store_name,
          slug: s.store_slug,
          image: s.store_image,
        };

        row[metric] = s[field];
        byId.set(s.store_id, row);
      });
    });

    // ترتیب و ۵ تای برتر را خود بک‌اند تعیین کرده
    const { list } = STORE_SORT_SOURCES[storeSort];

    return (data?.[list] ?? []).slice(0, 5).map((s) => byId.get(s.store_id));
  }, [data, storeSort]);

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div dir="rtl">
      <main>
        {loading ? (
          <div className="adminx-root" dir="rtl">
            <div className="adminx-state">در حال بارگذاری...</div>
          </div>
        ) : !data ? (
          <div className="adminx-root" dir="rtl">
            <div className="adminx-state adminx-state-error">
              خطا در دریافت اطلاعات
            </div>
          </div>
        ) : (
          <div className="adminx-root" dir="rtl">
            {/* <Toast notifications={notifications} onDismiss={dismiss} /> */}

            {/* =================================================
                HEADER
               ================================================= */}

            <header className="adminx-header">
              <div className="adminx-title-wrap">
                <h1 className="adminx-title">داشبورد ادمین</h1>

                <p className="adminx-subtitle">
                  نمای کلی وضعیت فروشگاه‌ها، کاربران، پرداخت‌ها و پشتیبانی
                </p>
              </div>

              <div className="adminx-header-actions">
                <div className="adminx-date-badge">گزارش مدیریتی</div>

                <a
                  className="dash-dokana-btn"
                  href="/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon>{ICONS.externalLink}</Icon>
                  ورود به سایت دکانا
                </a>
              </div>
            </header>

            {/* =================================================
                PAYMENT CARDS
               ================================================= */}

            <section className="adminx-payment-grid">
              {[
                {
                  label: "کل واریزی",
                  val: `${Number(data.summary.total_payment_all).toLocaleString(
                    "fa-IR",
                  )} تومان`,
                },
                {
                  label: "واریزی امسال",
                  val: `${Number(
                    data.summary.total_payment_year,
                  ).toLocaleString("fa-IR")} تومان`,
                },
                {
                  label: "واریزی این ماه",
                  val: `${Number(
                    data.summary.total_payment_month,
                  ).toLocaleString("fa-IR")} تومان`,
                },
                {
                  label: "واریزی امروز",
                  val: `${Number(
                    data.summary.total_payment_today,
                  ).toLocaleString("fa-IR")} تومان`,
                },
              ].map(({ label, val }) => (
                <div key={label} className="adminx-payment-card">
                  <div className="adminx-payment-icon">
                    <FiDollarSign />
                  </div>

                  <div className="adminx-payment-content">
                    <p className="adminx-payment-label">{label}</p>

                    <p className="adminx-payment-value">{val}</p>
                  </div>
                </div>
              ))}
            </section>

            {/* =================================================
                STAT CARDS
               ================================================= */}

            <section className="adminx-stat-grid">
              {[
                {
                  label: "فروشگاه‌ها",
                  val: data.summary.store_count,
                  icon: <FiShoppingBag />,
                },
                {
                  label: "کاربران",
                  val: data.summary.user_count,
                  icon: <FiUsers />,
                },
                {
                  label: "مارکترها",
                  val: data.summary.marketer_count,
                  icon: <FiTrendingUp />,
                },
                {
                  label: "ساپورترها",
                  val: data.summary.support_count,
                  icon: <FiHeadphones />,
                },
                {
                  label: "تیکت باز",
                  val: data.summary.open_tickets,
                  icon: <FiMessageSquare />,
                },
                {
                  label: "صف پاسخ",
                  val: data.summary.waiting_tickets,
                  icon: <FiClock />,
                },
              ].map(({ label, val, icon }) => (
                <div key={label} className="adminx-stat-card">
                  <div className="adminx-stat-icon">{icon}</div>

                  <p className="adminx-stat-label">{label}</p>

                  <p className="adminx-stat-value">
                    {Number(val).toLocaleString("fa-IR")}
                  </p>
                </div>
              ))}
            </section>

            {/* =================================================
                CHART GRID
               ================================================= */}

            <section className="adminx-chart-grid">
              {/* STORES BY MONTH */}

              <ChartCard
                title="تعداد فروشگاه به تفکیک ماه"
                filterKey="store_year"
                filters={filters}
                jalaliYears={jalaliYears}
                onChange={set}
              >
                <AreaChart
                  data={(data.stores_by_month || []).map((d) => ({
                    ...d,
                    month: MONTHS[d.month - 1],
                  }))}
                  margin={{
                    top: 24,
                    right: 8,
                    left: 8,
                    bottom: 12,
                  }}
                >
                  <defs>
                    <linearGradient
                      id="storeGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="var(--mauve-700)"
                        stopOpacity={0.3}
                      />

                      <stop
                        offset="100%"
                        stopColor="var(--mauve-700)"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    stroke="rgba(43, 18, 76, 0.09)"
                    strokeDasharray="4 4"
                    vertical
                    horizontal
                  />

                  <XAxis
                    dataKey="month"
                    interval={0}
                    tickLine={false}
                    axisLine={{
                      stroke: "rgba(43, 18, 76, 0.15)",
                    }}
                    tick={<MonthTick />}
                    height={95}
                  />

                  <YAxis
                    allowDecimals={false}
                    axisLine={{
                      stroke: "rgba(43, 18, 76, 0.15)",
                    }}
                    tickLine={false}
                    tick={yAxisTick}
                    width={40}
                  />

                  <Tooltip content={<ChartTooltip unit="فروشگاه" />} />

                  <Area
                    type="monotone"
                    dataKey="count"
                    name="فروشگاه"
                    stroke="var(--mauve-700)"
                    strokeWidth={2.5}
                    fill="url(#storeGradient)"
                    dot={{
                      r: 3.5,
                      fill: "var(--white)",
                      stroke: "var(--mauve-700)",
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 5.5,
                      fill: "var(--rose-500)",
                      stroke: "var(--rose-500)",
                    }}
                  />
                </AreaChart>
              </ChartCard>

              {/* PAYMENTS BY MONTH */}

              <ChartCard
                title="مبلغ واریزی به تفکیک ماه"
                filterKey="payment_year"
                filters={filters}
                jalaliYears={jalaliYears}
                onChange={set}
              >
                <LineChart
                  data={(data.payments_by_month || []).map((d) => ({
                    ...d,
                    month: MONTHS[d.month - 1],
                  }))}
                  margin={{
                    top: 24,
                    right: 8,
                    left: 8,
                    bottom: 12,
                  }}
                >
                  <CartesianGrid
                    stroke="rgba(43, 18, 76, 0.09)"
                    strokeDasharray="4 4"
                    vertical
                    horizontal
                  />

                  <XAxis
                    dataKey="month"
                    interval={0}
                    tickLine={false}
                    axisLine={{
                      stroke: "rgba(43, 18, 76, 0.15)",
                    }}
                    tick={<MonthTick />}
                    height={95}
                  />

                  <YAxis
                    axisLine={{
                      stroke: "rgba(43, 18, 76, 0.15)",
                    }}
                    tickLine={false}
                    tick={yAxisTick}
                    width={45}
                  />

                  <Tooltip content={<ChartTooltip unit="تومان" />} />

                  <Line
                    type="monotone"
                    dataKey="total"
                    name="واریزی"
                    stroke="var(--mauve-700)"
                    strokeWidth={2.5}
                    dot={{
                      r: 3.5,
                      fill: "var(--white)",
                      stroke: "var(--mauve-700)",
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 5.5,
                      fill: "var(--rose-500)",
                      stroke: "var(--rose-500)",
                    }}
                  />
                </LineChart>
              </ChartCard>

              {/* TICKETS BY MONTH */}

              <ChartCard
                title="تعداد تیکت به تفکیک ماه"
                filterKey="ticket_year"
                filters={filters}
                jalaliYears={jalaliYears}
                onChange={set}
              >
                <BarChart
                  data={(data.tickets_by_month || []).map((d) => ({
                    ...d,
                    month: MONTHS[d.month - 1],
                  }))}
                  margin={{
                    top: 24,
                    right: 8,
                    left: 8,
                    bottom: 12,
                  }}
                >
                  <defs>
                    <linearGradient
                      id="ticketBarGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="var(--rose-500)" />

                      <stop offset="100%" stopColor="var(--mauve-700)" />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    stroke="rgba(43, 18, 76, 0.09)"
                    strokeDasharray="4 4"
                    vertical
                    horizontal
                  />

                  <XAxis
                    dataKey="month"
                    interval={0}
                    tickLine={false}
                    axisLine={{
                      stroke: "rgba(43, 18, 76, 0.15)",
                    }}
                    tick={<MonthTick />}
                    height={95}
                  />

                  <YAxis
                    allowDecimals={false}
                    axisLine={{
                      stroke: "rgba(43, 18, 76, 0.15)",
                    }}
                    tickLine={false}
                    tick={yAxisTick}
                    width={40}
                  />

                  <Tooltip content={<ChartTooltip unit="تیکت" />} />

                  <Bar
                    dataKey="count"
                    name="تیکت"
                    fill="url(#ticketBarGradient)"
                    radius={[8, 8, 8, 8]}
                    maxBarSize={32}
                  />
                </BarChart>
              </ChartCard>

              {/* VISITS + TOP STORES */}

              <div className="adminx-visits-panel">
                <div className="adminx-chart-head">
                  <h2 className="adminx-chart-title">
                    بازدید سایت دکانا{" "}
                    {visitsPeriod === "yearly" ? "سال" : "ماه"}
                  </h2>

                  <div className="adminx-toggle">
                    <button
                      type="button"
                      className={visitsPeriod === "monthly" ? "active" : ""}
                      onClick={() => setVisitsPeriod("monthly")}
                    >
                      روزانه
                    </button>

                    <button
                      type="button"
                      className={visitsPeriod === "yearly" ? "active" : ""}
                      onClick={() => setVisitsPeriod("yearly")}
                    >
                      ماهانه
                    </button>
                  </div>
                </div>

                <VisitsRadarChart data={visitsChartData} unit="بازدید" />
              </div>

              {/* STORES BY MARKETER */}

              <div className="adminx-marketer-stores-row">
                <div ref={marketerSectionRef} className="adminx-marketer-panel">
                  <div className="adminx-marketer-head">
                    <h2 className="adminx-marketer-title">
                      فروشگاه‌های ساخته‌شده به تفکیک مارکتر
                    </h2>

                    <div className="adminx-marketer-filters">
                      <select
                        className="adminx-month-select"
                        value={filters.marketer_month}
                        onChange={set("marketer_month")}
                      >
                        {MONTHS.map((month, index) => (
                          <option key={index} value={index + 1}>
                            {month}
                          </option>
                        ))}
                      </select>

                      <select
                        className="adminx-year-select"
                        value={filters.marketer_year}
                        onChange={set("marketer_year")}
                      >
                        {jalaliYears.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={data.stores_by_marketer || []}
                      margin={{
                        top: 24,
                        right: 8,
                        left: 8,
                        bottom: 0,
                      }}
                    >
                      <defs>
                        <linearGradient
                          id="marketerBarGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor="var(--rose-500)" />

                          <stop offset="100%" stopColor="var(--mauve-700)" />
                        </linearGradient>
                      </defs>

                      <CartesianGrid
                        stroke="rgba(43, 18, 76, 0.09)"
                        strokeDasharray="4 4"
                        vertical
                        horizontal
                      />

                      <XAxis
                        dataKey="name"
                        interval={0}
                        tickLine={false}
                        axisLine={{
                          stroke: "rgba(43, 18, 76, 0.15)",
                        }}
                        tick={axisTick}
                        angle={-35}
                        textAnchor="end"
                        height={65}
                        dy={6}
                      />

                      <YAxis
                        allowDecimals={false}
                        axisLine={{
                          stroke: "rgba(43, 18, 76, 0.15)",
                        }}
                        tickLine={false}
                        tick={yAxisTick}
                        width={40}
                      />

                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) {
                            return null;
                          }

                          const d = payload[0].payload;

                          const roleLabel =
                            d.role === "marketer" ? "مارکتر" : "کاربر";

                          return (
                            <div className="chart-tooltip">
                              <span className="chart-tooltip-label">
                                {d.name}
                              </span>

                              <span className="chart-tooltip-meta">
                                {roleLabel} — {d.phone}
                              </span>

                              <span className="chart-tooltip-value">
                                {Number(d.count).toLocaleString("fa-IR")}{" "}
                                فروشگاه
                              </span>
                            </div>
                          );
                        }}
                      />

                      <Bar
                        dataKey="count"
                        name="فروشگاه"
                        fill="url(#marketerBarGradient)"
                        radius={[8, 8, 8, 8]}
                        maxBarSize={32}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* TOP 5 ACTIVE STORES */}

                <div className="adminx-stores-panel">
                  <div className="adminx-chart-head">
                    <h2 className="adminx-chart-title">۵ فروشگاه برتر فعال</h2>

                    <div className="adminx-toggle">
                      {STORE_SORT_OPTIONS.map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          className={storeSort === key ? "active" : ""}
                          onClick={() => setStoreSort(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="adminx-table-scroll">
                    <div className="adminx-table">
                      <div className="adminx-table-row adminx-table-row-head">
                        <span>#</span>
                        <span>فروشگاه</span>
                        <span
                          className={storeSort === "visits" ? "is-sorted" : ""}
                        >
                          بازدید
                        </span>
                        <span
                          className={
                            storeSort === "customers" ? "is-sorted" : ""
                          }
                        >
                          مشتری
                        </span>
                        <span
                          className={storeSort === "sales" ? "is-sorted" : ""}
                        >
                          فروش (تومان)
                        </span>
                      </div>

                      {topStores.map((s, i) => (
                        <div className="adminx-table-row" key={s.id ?? i}>
                          <span className="adminx-table-rank">
                            {(i + 1).toLocaleString("fa-IR")}
                          </span>

                          <span className="adminx-table-name" title={s.name}>
                            <StoreAvatar src={s.image} name={s.name} />
                            <span className="adminx-table-name-text">
                              {s.name}
                            </span>
                          </span>

                          <span
                            className={
                              storeSort === "visits" ? "is-sorted" : ""
                            }
                          >
                            {formatStat(s.visits)}
                          </span>

                          <span
                            className={
                              storeSort === "customers" ? "is-sorted" : ""
                            }
                          >
                            {formatStat(s.customers)}
                          </span>

                          <span
                            className={storeSort === "sales" ? "is-sorted" : ""}
                          >
                            {formatStat(s.sales)}
                          </span>
                        </div>
                      ))}

                      {topStores.length === 0 && (
                        <p className="adminx-table-empty">
                          فروشگاه فعالی یافت نشد.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

/* =========================================================
   CHART CARD
   ========================================================= */

function ChartCard({
  title,
  filterKey,
  filters,
  jalaliYears,
  onChange,
  children,
}) {
  return (
    <div className="adminx-chart-panel">
      <div className="adminx-chart-head">
        <h2 className="adminx-chart-title">{title}</h2>

        <select
          className="adminx-year-select"
          value={filters[filterKey]}
          onChange={onChange(filterKey)}
        >
          {jalaliYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      <div className="adminx-chart-box">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
